import type { Campaign } from '@/lib/types';

type CompletionContext = 'just_completed' | 'already_completed' | null | undefined;

const DEFAULT_COMPLETION_HEADLINE = "You're all set!";
const DEFAULT_COMPLETION_BODY =
  'Thanks for sharing your thoughts today. Your feedback helps improve care for everyone.';
const DEFAULT_ALREADY_COMPLETED_BODY =
  'You already shared your thoughts with us — thank you. Your feedback is helping improve the care experience for everyone.';

/** Returns true if the string is empty or contains only empty HTML tags (e.g. "<p></p>") */
function isBlankHtml(value: string): boolean {
  const text = value.replace(/<[^>]*>/g, '').trim();
  return text.length === 0;
}

function firstDefined(...values: Array<string | null | undefined>): string | null | undefined {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    // An explicit empty string is an intentional blank — preserve it.
    // Only skip strings that contain nothing but empty HTML tags (e.g. "<p></p>").
    if (value === '' || !isBlankHtml(value)) {
      return value;
    }
  }
  return undefined;
}

export function resolveCompletionCopy(
  campaign: Campaign | null | undefined,
  completionContext: CompletionContext,
): { headline: string; body: string | null } {
  const wrapup = campaign?.wrapup;

  if (completionContext === 'already_completed') {
    return {
      headline: firstDefined(
        wrapup?.already_completed_headline,
        wrapup?.completion_headline,
        DEFAULT_COMPLETION_HEADLINE,
      )!,
      body: firstDefined(
        wrapup?.already_completed_body,
        wrapup?.completion_body,
        DEFAULT_ALREADY_COMPLETED_BODY,
      )!,
    };
  }

  return {
    headline: firstDefined(
      wrapup?.completion_headline,
      DEFAULT_COMPLETION_HEADLINE,
    )!,
    body: firstDefined(
      wrapup?.completion_body,
      DEFAULT_COMPLETION_BODY,
    )!,
  };
}
